import { FC, useEffect, useState, Fragment, useRef, useMemo } from "react";
// 组件
import { DndContext, DragEndEvent, useSensors, useSensor, MouseSensor, KeyboardSensor, closestCenter, } from '@dnd-kit/core';
import { ProModal, ProForm, ProNotification, } from '../antd';
import CardHeader from "./subpage/cardHeader";
import CardDidGroup from "./subpage/cardDidGroup";
import CardGroup from './subpage/cardGroup';
import CardFooter from "./subpage/cardFooter";
// 方法
import { SortableContext, arrayMove, sortableKeyboardCoordinates, } from '@dnd-kit/sortable'
import { filterObjEmptyValue } from "../utils/utils";
// 常量
import { TABLE_COLUMN_EMPTY, STATUS_CREATED, STATUS_CREATED_CODE, } from '../constants/constant';
import { CardFormProps, ID_KEY, CARD_FORM_NAME, } from "./interface";

/** 卡片表单 */
const CardFormInter: FC<CardFormProps> = props => {
  const {
    actionRef, formItems, dataSource: outDataSource, defaultValue, title, subTitle, addIcon, addText, showAdd, toolTipText, isMoveSort, isDelete,
    deleteText, disabled, sortFieldName, maxLength, maxLengthText, sliceModalTitle, onValuesChange, onAdd, formatChangeValue,
  } = props;
  const [dataSource, setDataSource] = useState<any[] | undefined>(defaultValue || []);
  const [form] = ProForm.useForm();
  const lastKey = useRef<number>(0);
  const modalShow = useRef<boolean>(false);
  useEffect(() => {
    lastKey.current = Array.isArray(outDataSource) ? outDataSource.length : 0;
    setDataSource(outDataSource?.map((item, key) => ({
      ...item,
      [ID_KEY]: key + 1,
      [STATUS_CREATED_CODE]: item[STATUS_CREATED_CODE] || STATUS_CREATED.DEFAULT,
    })));
  }, [outDataSource]);
  useEffect(() => {
    if (dataSource && maxLength !== undefined) {
      const valueLength = dataSource.length;
      if (valueLength > maxLength) {
        const modalTitle = typeof sliceModalTitle === 'function' ? sliceModalTitle(dataSource) : sliceModalTitle;
        if (modalShow.current === false) {
          if (!!modalTitle) {
            ProModal.confirm({
              title: modalTitle,
              onCancel: () => {
                modalShow.current = false;
              },
              onOk: () => {
                modalShow.current = false;
                setDataSource(dataSource.slice(0, maxLength));
              },
            });
          } else {
            setDataSource(dataSource.slice(0, maxLength));
          }
          modalShow.current = true;
        }
      }
    }
  }, [dataSource, maxLength]);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, }),
  );
  /** 清空数据 */
  const handleEmpty = () => {
    setDataSource(undefined);
  };
  /** 点击新增按钮 */
  const handleAdd = (data: any = {}) => {
    if (onAdd) {
      const isAdd = onAdd();
      if (isAdd === false) return;
    }
    setDataSource(prev => {
      const newValue = prev ? [...prev] : [];
      if (Array.isArray(data)) {
        data.forEach(item => {
          lastKey.current = lastKey.current + 1;
          newValue.push({
            ...item,
            [ID_KEY]: lastKey.current,
            [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
          });
        });
      } else {
        lastKey.current = lastKey.current + 1;
        newValue.push({
          ...data,
          [ID_KEY]: lastKey.current,
          [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
        });
      }
      return newValue;
    });
  };
  /** 表单值修改回调 */
  const handleChange = (groupKey: number, groupValues: any) => {
    setDataSource(prev => prev?.map(item => {
      if (item[ID_KEY] === groupKey) return groupValues;
      return item;
    }));
  };
  /** 删除行 */
  const handleDelete = (groupKey: number[]) => {
    setDataSource((prev?: any[]) => {
      if (!prev) return;
      const newValue: any = [];
      prev.forEach(item => {
        if (groupKey.includes(item[ID_KEY])) {
          if (item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD) {
            newValue.push({
              ...item,
              [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
            });
          }
          return;
        }
        newValue.push(item);
      });
      return newValue;
    });
  };
  /** 拖动 */
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!dataSource || !active || !over) return;
    if (active.id !== over.id) {
      const oldIndex = dataSource.findIndex(item => item[ID_KEY] === active.id);
      const newIndex = dataSource.findIndex(item => item[ID_KEY] === over.id);
      const newValue = arrayMove(dataSource, oldIndex, newIndex).map((item, index) => {
        if ((index === oldIndex || index === newIndex) && item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD) {
          return { ...item, [STATUS_CREATED_CODE]: STATUS_CREATED.UPDATE, };
        }
        return item;
      });
      setDataSource(newValue);
    }
  };
  /** 可编辑表字段值更新 */
  const handleValuesChange = (changedValues: any, values: any) => {
    if (!onValuesChange) return;
    const changeData = changedValues?.[CARD_FORM_NAME];
    const [changeKey, changeOption] = Object.entries(changeData)?.[0];
    let [changedField, changedValue] = Object.entries(changeOption as any)[0];
    const currentRowKey = Number(changeKey);
    const findDataSource = dataSource?.find(item => item.key === currentRowKey);
    onValuesChange?.(changedField, changedValue, currentRowKey, { ...findDataSource, ...values[CARD_FORM_NAME][currentRowKey] });
  };
  /** 获取卡片数据 */
  const handleGetCardData = (isEffective?: boolean) => {
    const cardData: any[] = [];
    dataSource?.forEach(item => {
      const formItemData = form.getFieldValue([CARD_FORM_NAME, item[ID_KEY]]);
      const newItem = {...item, ...formItemData};
      if (isEffective && newItem[STATUS_CREATED_CODE] === STATUS_CREATED.DELETE) {
        return;
      }
      cardData.push(newItem);
    });
    return cardData;
  };
  /** 提交 */
  const handleSubmit = async () => {
    try {
      const formData = await form.validateFields();
      const effectiveCardData: any[] = [];
      const deleteCardData: any[] = [];
      dataSource?.forEach((item, index) => {
        const formItemData = formData?.[CARD_FORM_NAME]?.[item[ID_KEY]];
        const cardItemData = filterObjEmptyValue({...item, ...formItemData});
        delete cardItemData[ID_KEY];
        if (isMoveSort && sortFieldName) {
          cardItemData[sortFieldName] = index + 1;
        }
        if (item[STATUS_CREATED_CODE] === STATUS_CREATED.DELETE) {
          deleteCardData.push(cardItemData);
        } else {
          effectiveCardData.push(cardItemData);
        }
      });
      return [...deleteCardData, ...effectiveCardData];
    } catch (errorInfo: any) {
      if (Array.isArray(errorInfo.errorFields)) {
        const messageNode = (
          <>
            {errorInfo.errorFields.map((v: any) => (
              <div key={v.name[0]}>{v.errors[0]}</div>
            ))}
          </>
        );
        ProNotification.warning({
          message: messageNode,
          className: 'detail-page-require',
        });
      }
      return false;
    }
  };
  const effectiveDataSource = useMemo(() => {
    return dataSource?.filter(item => item[STATUS_CREATED_CODE] !== STATUS_CREATED.DELETE);
  }, [dataSource]);
  const isMaxLength = useMemo(() => {
    if (maxLength === undefined || !effectiveDataSource) return false;
    if (effectiveDataSource.length < maxLength) return false;
    return true;
  }, [maxLength, effectiveDataSource]);

  const renderNode = (
    <ProForm
      component={false}
      form={form}
      onValuesChange={handleValuesChange}
    >
      <div className="form-card">
        <CardHeader
          title={title}
          subTitle={subTitle}
          toolTipText={toolTipText}
        />
        {
          Array.isArray(effectiveDataSource) && effectiveDataSource.length ? (
            effectiveDataSource.map(groupValue => {
              return (
                <Fragment key={groupValue.key}>
                  {
                    isMoveSort ? (
                      <CardDidGroup
                        form={form}
                        groupKey={groupValue.key}
                        value={groupValue}
                        isDelete={isDelete}
                        deleteText={deleteText}
                        formItems={formItems}
                        disabled={disabled}
                        onChange={handleChange}
                        onDelete={handleDelete}
                        formatChangeValue={formatChangeValue}
                      />
                    ) : (
                      <CardGroup
                        form={form}
                        groupKey={groupValue.key}
                        value={groupValue}
                        disabled={disabled}
                        isDelete={isDelete}
                        deleteText={deleteText}
                        formItems={formItems}
                        onChange={handleChange}
                        onDelete={handleDelete}
                        formatChangeValue={formatChangeValue}
                      />
                    )
                  }
                </Fragment>
              );
            })
          ) : (
            disabled ? <div className="form-card-empty">{TABLE_COLUMN_EMPTY}</div> : null
          )
        }
        <CardFooter
          addIcon={addIcon}
          addText={addText}
          showAdd={showAdd}
          disabled={disabled}
          isMaxLength={isMaxLength}
          maxLengthText={maxLengthText}
          onAdd={handleAdd}
        />
      </div>
    </ProForm>
  );
  if (actionRef) {
    actionRef.current = {
      add: handleAdd,
      delete: (record => {
        if (Array.isArray(record)) {
          handleDelete(record.map(item => item[ID_KEY]));
          return;
        }
        handleDelete(record[ID_KEY]);
      }),
      getCardData: handleGetCardData,
      submit: handleSubmit,
      empty: handleEmpty,
      setFieldValue: (groupKey: number, field: string, value: any) => {
        if (groupKey === undefined || !field) return;
        form.setFieldValue([CARD_FORM_NAME, groupKey, field], value);
      },
      setFieldsValue: (groupKey: number, values: any) => {
        if (groupKey === undefined) return;
        const rowFormData = form.getFieldValue([CARD_FORM_NAME, groupKey]);
        form.setFieldValue([CARD_FORM_NAME, groupKey], { ...rowFormData, ...values, });
      },
      setRowsValue: (fieldName: string, fieldValue: any) => {
        dataSource?.forEach(item => {
          form.setFieldValue([CARD_FORM_NAME, item.key, fieldName], fieldValue);
        });
      },
    };
  }

  return (
    isMoveSort ? (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={dataSource?.map(item => item[ID_KEY]) || []}
        >
          {renderNode}
        </SortableContext>
      </DndContext>
    ) : renderNode
  );
};

CardFormInter.defaultProps = {
  isMoveSort: true,
  isDelete: true,
  deleteText: '移除',
  showAdd: true,
  sortFieldName: 'displayOrder',
};

export default CardFormInter;
